import { RouteComponentProps } from '@reach/router';
import {
  Collection,
  Product as OriginalProduct,
  ProductVariant as OriginalProductVariant,
} from 'shopify-storefront-api-typings';
import type {
  GenericFile,
  Maybe,
  MediaConnection,
  Model3d,
  ProductEdge as OriginalProductEdge,
} from '@shopify/hydrogen-react/storefront-api-types';
import { ProductKey } from '@/themes/types';

/**
 * Shopify Types
 * ------------------------------------------------------------------------------
 *  TODO: Shopify has just open sourced Hydrogen React including updated types for all Shopify objects
 *     AND a lighweight Storefront API Client built into the framework. PLEASE CONSIDER moving
 *     to this new framework as soon as possible so we can stay ahead of any competitors and avoid
 *     getting mired in our own technical debt.
 *  SEE: @shopify/hydrogen-react
 *  Example:
 *     import type {Metafield as MetafieldV2} from '@shopify/hydrogen-react/storefront-api-types';
 *     export type MetafieldV2 = MetafieldV2;
 */

interface Metafield {
  value: string;
  key: string;
}

type Metafields = Array<Metafield | null>;

type PageSEO = {
  title?: string;
  description?: string;
};

type FileReference = {
  reference?: Maybe<GenericFile>;
};

type Model3dReference = {
  reference?: Maybe<Model3d>;
};

export type MediaReferences = {
  references?: Maybe<MediaConnection>;
};

interface ValuePropNode {
  id: string;
  copy: {
    value: string;
  };
  svg_icon: {
    reference: {
      id: string;
      alt?: string;
      image: {
        url: string;
      };
    };
  };
}

interface ValuePropsReferences {
  references?: {
    edges: Array<{
      node: ValuePropNode;
    }>;
  };
}

interface OrientationReference {
  references?: {
    edges: Array<{
      node: {
        orientation_type: {
          value: string;
        };
      } & Product;
    }>;
  };
}

export type MetafieldImageReference = {
  reference?: {
    image?: {
      url: string;
    };
  };
};

export type ProductWithColorImage = OriginalProduct & {
  color_image?: MetafieldImageReference;
};

type Product = Omit<OriginalProduct, 'metafields'> & {
  metafields?: Metafields;
  mat_variants?: any;
  mat_product_group?: any;
  mat_image?: MetafieldImageReference;
  color_image?: MetafieldImageReference;
  hallway_size_product?: any;
  seo?: PageSEO;
  slider_images?: MediaReferences;
  showcase_items?: MediaReferences;
  value_props?: ValuePropsReferences;
  orientation_variants?: OrientationReference;
  pdp_product_group?: any;
  editorial_section?: any;
  product_type_display_name?: any;
  gallery_sizes_group?: any;
  pdp_accordion_custom_block?: any;
};

type ProductImage = {
  src?: string;
  id?: string | number;
};

type ProductVariant = Omit<OriginalProductVariant, 'metafields'> & {
  metafields?: Metafields;
  model_file?: FileReference | Model3dReference;
};

export interface RouteProps extends RouteComponentProps {
  handle?: string;
  product: Product;
  shopMetafields?: Metafields;
  pageType?: ProductKey;
  colorCollection?: Collection;
}

export type PrintSize = string;
export type FrameScale = {
  frame_size: string;
  scale_factor: number;
  v2: { [key: string]: number };
};

export enum ProductTypes {
  GALLERY = 'gallery',
  ARTISTIC = 'artistic-print',
  PICTURE = 'picture-frame',
  METAL = 'metal-print',
  ACRYLICBLOCK = 'acrylic-block',
  ACRYLICPRINT = 'acrylic-print',
  ACRYLICSTANDOFF = 'acrylic-standoff',
  CANVASWRAP = 'canvas-wrap',
  FRAMEDCANVAS = 'framed-canvas-wrap',
  PHOTOTILEWRAP = 'photo-tile-wrap',
  PHOTOTILE = 'photo-tile',
  INLAY = 'photo-inlay',
  BLACKLABEL = 'black-label-frame',
  GLOSSWOODPRINT = 'gloss-wood-print',
  GLASSPRINT = 'glass-print',
  DESIGNSERVICE = 'design-service',
  GIFTCARDS = 'gift-cards',
  POUCH = 'pouch',
  PUZZLE = 'puzzle',
  METALDESKPRINT = 'metal-desk-print',
  TEDDYBEAR = 'teddy-bear',
  ACRYLICDESKPRINT = 'acrylic-desk-print',
  MUG = 'mug',
  STEELBOTTLE = 'steel-bottle',
  BOTTLE = 'bottle',
  GLASSORNAMENT = 'glass-ornament',
  METALORNAMENT = 'metal-ornament',
  SNOWFLAKEORNAMENT = 'snowflake-ornament',
  ORNAMENT = 'ornament',
  MAGNET = 'magnet',
}

export enum FramelessTypes {
  'metal-print',
  'acrylic-block',
  'acrylic-print',
  'acrylic-standoff',
  'canvas-wrap',
  'framed-canvas-wrap',
  'photo-tile-wrap',
  'photo-tile',
}

interface ProductWithFeaturedImage extends OriginalProduct {
  featuredImage?: {
    url: string;
  };
}

type ProductEdge = Omit<OriginalProductEdge, 'node'> & {
  node: ProductWithFeaturedImage;
};

// type ProductTypes = "gallery" | "picture-frame" | "metal-print";
export type ProductType = { [key in ProductTypes]: ProductTypes };

export type FramelessType = { [key in FramelessTypes]: FramelessTypes };

export type { Metafields, Product, ProductVariant, ProductImage, ProductEdge };

export type Orientation = 'portrait' | 'landscape' | 'square';
